import React, { useState, useCallback, useRef, useEffect } from "react";
import { useAtelierStore } from "@store/atelierStore";
import { useDispoAtelierStore } from "@store/dispoAtelierStore";
import { useDataStore } from "@store/user_org";
import { isOwnerOrAdmin } from "@utils/permissions";
import { WorkCharge } from "../../types/garage/atelier";
import { ATELIER_FILTER_METIERS, METIER_META } from "../../types/garage/metier";
import { ChunkyTabs } from "@components/ui/selection/ChunkyTabs";
import type { CompactionMode } from "@store/planningStore";
import SearchAlgolia from "@components/ui/search/SearchAlgolia";
import MiniCalendar from "@components/ui/MiniCalendar";
import { searchJobsFull } from "@services/algolia";
import { ordresApi } from "@services/api/ordres";
import { showNotification } from "context/notification.service";
import OrdreModal from "@components/modals/OrdreModal";
import type { Ordre } from "types/ordre";
import {
  ChargeSection,
  ChargeDropZone,
  StatusLegend,
} from "./ChargesSidebar";
import { Text1, Text2 } from "@components/ui/texts/Texts";
import { ChargeModal } from "./SplitModal";
import { FloatingGhostOverlay } from "./FloatingGhostOverlay";
import { WorkerTimeGrid } from "./WorkerTimeGrid";
import { WeekTimeGridAtelier } from "./WeekTimeGridAtelier";
import { useAtelierDrag } from "./useAtelierDrag";
import LunchOverlapModal from "./LunchOverlapModal";
import MetierMismatchModal from "./MetierMismatchModal";
import ReportPauseModal from "./ReportPauseModal";
import JobMergeModal from "./JobMergeModal";
import hintDragIcon from "@assets/icons/hint-drag.svg";
import hintDropGif from "@assets/gifs/hint-drop.gif";
import hintLeftIcon from "@assets/icons/hint-left.svg";

export default function AtelierScreen() {
  const orgData = useDataStore((s) => s.orgData);
  const userData = useDataStore((s) => s.userData);
  const fetchData = useAtelierStore((s) => s.fetchData);
  const startRealtimeListener = useAtelierStore((s) => s.startRealtimeListener);
  const stopRealtimeListener = useAtelierStore((s) => s.stopRealtimeListener);
  const fetchBlocks = useDispoAtelierStore((s) => s.fetchBlocks);
  const blocks = useDispoAtelierStore((s) => s.blocks);
  useEffect(() => {
    if (orgData?.id) {
      fetchData(orgData.id, true).then(() => {
        startRealtimeListener(orgData.id);
      });
      fetchBlocks(orgData.id);
    }
    return () => {
      stopRealtimeListener();
    };
  }, [orgData?.id]);

  const selectedDate = useAtelierStore((s) => s.selectedDate);
  const setSelectedDate = useAtelierStore((s) => s.setSelectedDate);
  const viewMode = useAtelierStore((s) => s.viewMode);
  const setViewMode = useAtelierStore((s) => s.setViewMode);
  const compaction = useAtelierStore((s) => s.compaction);
  const setCompaction = useAtelierStore((s) => s.setCompaction);
  const activeSpecialties = useAtelierStore((s) => s.activeSpecialties);
  const toggleSpecialty = useAtelierStore((s) => s.toggleSpecialty);
  const setAllSpecialties = useAtelierStore((s) => s.setAllSpecialties);
  const [algoliaQuery, setAlgoliaQuery] = useState("");
  const charges = useAtelierStore((s) => s.charges);
  const carWindows = useAtelierStore((s) => s.carWindows);
  const workers = useAtelierStore((s) => s.workers);
  const selectedWeekWorkerId = useAtelierStore((s) => s.selectedWeekWorkerId);
  const setSelectedWeekWorkerId = useAtelierStore((s) => s.setSelectedWeekWorkerId);
  const selectedWorkerIds = useAtelierStore((s) => s.selectedWorkerIds);
  const toggleWorkerId = useAtelierStore((s) => s.toggleWorkerId);
  const setSelectedWorkerIds = useAtelierStore((s) => s.setSelectedWorkerIds);

  // Worker pills follow the active métier filter (carrosserie → carrossiers only…).
  const metierWorkers =
    activeSpecialties.length === 0
      ? workers
      : workers.filter((w) => activeSpecialties.includes(w.specialty));

  // Charges are filtered to the viewed day (or Mon–Fri week) via the window
  // during which the vehicle is on site — from its reception (depositDate) to its
  // restitution — and to the selected métier, mirroring the jobs grid so you only
  // see charges relevant to what's on screen (not the whole org backlog). A car
  // present Mon→Thu shows its charge on every one of those days, not just Monday.
  const _pad = (n: number) => String(n).padStart(2, "0");
  const _toISO = (d: Date) => `${d.getFullYear()}-${_pad(d.getMonth() + 1)}-${_pad(d.getDate())}`;
  // Visible Mon–Fri week as an ISO [start, end] range (avoids iterating a Set —
  // downlevel-iteration is off in the prod build's tsconfig).
  const weekRange = (() => {
    const d = new Date(selectedDate);
    const dow = d.getDay();
    const mon = new Date(d);
    mon.setDate(d.getDate() + (dow === 0 ? -6 : 1 - dow));
    const fri = new Date(mon);
    fri.setDate(mon.getDate() + 4);
    return { start: _toISO(mon), end: _toISO(fri) };
  })();
  const chargeMatchesView = (c: WorkCharge) => {
    if (activeSpecialties.length > 0 && !activeSpecialties.includes(c.specialty)) return false;
    const cw = c.carWindow || (c.ordre_id ? carWindows[c.ordre_id] : null);
    const start = cw?.depositDate ? cw.depositDate.slice(0, 10) : null;
    if (!start) return true; // undated charge — never hide (can't be placed in time)
    // Vehicle is present every day in [reception, restitution]. No restitution or
    // a bad (earlier) one → treat as a single day at reception.
    const restRaw = cw?.restitutionDate ? cw.restitutionDate.slice(0, 10) : null;
    const end = restRaw && restRaw >= start ? restRaw : start;
    // Week view: does [start, end] overlap the visible Mon–Fri week? Day view:
    // is the selected day inside the window?
    if (viewMode === "semaine") {
      return start <= weekRange.end && end >= weekRange.start;
    }
    return selectedDate >= start && selectedDate <= end;
  };

  const chargesARepartir = charges.filter((c) => c.status === "a_repartir" && chargeMatchesView(c));
  const chargesReportees = charges.filter((c) => c.status === "reportee" && chargeMatchesView(c));
  const chargesEnPause = charges.filter((c) => c.status === "en_pause" && chargeMatchesView(c));

  const [splitTarget, setSplitTarget] = useState<WorkCharge | null>(null);
  const [selectedOrdre, setSelectedOrdre] = useState<Ordre | null>(null);

  // Fullscreen: hide app chrome (nav, page title, charges sidebar) and let the
  // grid fill the whole viewport. Exit with the button or the Escape key.
  const [fullscreen, setFullscreen] = useState(false);
  useEffect(() => {
    if (!fullscreen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setFullscreen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [fullscreen]);

  const gridScrollRef = useRef<HTMLDivElement | null>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);

  const {
    jobDragRef,
    gridPreview,
    setGridPreview,
    floatingGhost,
    hoveredChargeSection,
    mergeTargetChargeId,
    isDraggingJob,
    handleGlobalPointerMove,
    handleGlobalPointerUp,
    handleChargeDragStart,
    overDropZone,
    lunchOverlapPending,
    setLunchOverlapPending,
    reportPausePending,
    setReportPausePending,
    jobMergePending,
    setJobMergePending,
    metierMismatchPending,
    setMetierMismatchPending,
    confirmMetierMismatch,
  } = useAtelierDrag(gridScrollRef, sidebarRef);

  const updateJob = useAtelierStore((s) => s.updateJob);
  const placeChargeAsJob = useAtelierStore((s) => s.placeChargeAsJob);
  const mergeJobs = useAtelierStore((s) => s.mergeJobs);
  const moveChargeToStatus = useAtelierStore((s) => s.moveChargeToStatus);

  const handleSplitClick = useCallback((charge: WorkCharge) => {
    setSplitTarget(charge);
  }, []);

  return (
    <div
      className={`flex flex-col h-screen overflow-auto ${fullscreen ? "fixed inset-0 z-[9998]" : ""}`}
      style={{ background: "#FAFAFA" }}
      onPointerMove={handleGlobalPointerMove}
      onPointerUp={handleGlobalPointerUp}
    >
      <div className="flex flex-col h-fit">
        {/* Title (hidden in fullscreen to give the grid maximum height) */}
        {!fullscreen && (
          <div className="flex flex-col px-4 lg:px-10 pt-8">
            <Text1>Planning atelier</Text1>
            <Text2>Répartissez les interventions ici.</Text2>
          </div>
        )}

        <div
          className={`flex flex-row flex-1 px-4 lg:px-10 ${fullscreen ? "pt-3" : "pt-5"}`}
          style={{
            gap: 30,
            overflow: "hidden",
            minHeight: 0,
          }}
        >
          {/* Left column (charges sidebar) — hidden in fullscreen */}
          <div
            ref={sidebarRef}
            className="flex flex-col"
            style={{
              width: 196,
              flexShrink: 0,
              gap: 20,
              overflowY: "auto",
              paddingBottom: 30,
              display: fullscreen ? "none" : "flex",
            }}
          >
            <MiniCalendar
              selectedDate={selectedDate}
              onSelectDate={setSelectedDate}
              weekHighlight={viewMode === "semaine"}
              showNavButtons
            />
            <ChargeSection
              title="Charges à répartir"
              status="a_repartir"
              charges={chargesARepartir}
              onSplitClick={handleSplitClick}
              onChargeDragStart={handleChargeDragStart}
              isDropTarget={hoveredChargeSection === "a_repartir"}
              mergeTargetChargeId={mergeTargetChargeId}
            />
            <ChargeSection
              title="Charges reportées"
              status="reportee"
              charges={chargesReportees}
              onSplitClick={handleSplitClick}
              onChargeDragStart={handleChargeDragStart}
              isDropTarget={hoveredChargeSection === "reportee"}
              mergeTargetChargeId={mergeTargetChargeId}
            />
            <ChargeSection
              title="Charges en pause"
              status="en_pause"
              charges={chargesEnPause}
              onSplitClick={handleSplitClick}
              onChargeDragStart={handleChargeDragStart}
              isDropTarget={hoveredChargeSection === "en_pause"}
              mergeTargetChargeId={mergeTargetChargeId}
            />
            {isDraggingJob && <ChargeDropZone active={overDropZone} />}
            <StatusLegend />
          </div>

          {/* Right column */}
          <div
            className="flex flex-col flex-1"
            style={{
              gap: 10,
              minWidth: 0,
              overflowY: "auto",
              paddingBottom: 30,
            }}
          >
            <div className="flex flex-row items-center" style={{ gap: 10 }}>
              <div style={{ width: 400, flexShrink: 0 }}>
                <SearchAlgolia
                  value={algoliaQuery}
                  setValue={setAlgoliaQuery}
                  placeholder="Rechercher un job..."
                  classNameCustom="w-full"
                  onSearch={async (query) => {
                    if (!orgData?.id) return [];
                    return searchJobsFull(query, orgData.id);
                  }}
                  onAction={(option) => {
                    if (typeof option === "object" && option.value) {
                      const jobData = option.data;
                      const date = jobData?.date;
                      const workerId = jobData?.worker_id;
                      if (date) setSelectedDate(date);
                      if (viewMode === "semaine" && workerId) {
                        setSelectedWeekWorkerId(workerId);
                      }
                      // Highlight the specific job and scroll to it (modal opens on click)
                      useAtelierStore.getState().setHighlightJobId(option.value);
                      return true;
                    }
                  }}
                />
              </div>
              <ChunkyTabs
                activeTab={viewMode}
                tabs={[
                  { value: "jour", label: "Jour" },
                  { value: "semaine", label: "Semaine" },
                ]}
                setActiveTab={(tab) => setViewMode(tab as "jour" | "semaine")}
              />
              <AtelierCompactionSelector compaction={compaction} setCompaction={setCompaction} />
              <button
                onClick={() => setFullscreen((f) => !f)}
                title={fullscreen ? "Quitter le plein écran (Échap)" : "Plein écran"}
                className="flex items-center justify-center w-9 h-9 rounded-lg border border-neutral-200 bg-white text-neutral-500 hover:text-neutral-900 hover:bg-neutral-50 transition-colors flex-shrink-0"
              >
                {fullscreen ? (
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M7 2v3.5A1.5 1.5 0 0 1 5.5 7H2M16 7h-3.5A1.5 1.5 0 0 1 11 5.5V2M11 16v-3.5a1.5 1.5 0 0 1 1.5-1.5H16M2 11h3.5A1.5 1.5 0 0 1 7 12.5V16"
                      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M2 6.5V3a1 1 0 0 1 1-1h3.5M16 6.5V3a1 1 0 0 0-1-1h-3.5M2 11.5V15a1 1 0 0 0 1 1h3.5M16 11.5V15a1 1 0 0 1-1 1h-3.5"
                      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </button>
            </div>
            {/* Multi-select métier filter: "Tous" + cumulable pills (incl. service rapide & campagnes) */}
            <div className="flex flex-row flex-wrap gap-1">
              <button
                onClick={setAllSpecialties}
                className={`px-4 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                  activeSpecialties.length === 0
                    ? "bg-white text-black shadow border border-neutral-200"
                    : "text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100"
                }`}
                style={{ fontFamily: "Poppins" }}
              >
                Tous
              </button>
              {ATELIER_FILTER_METIERS.map((m) => {
                const isActive = activeSpecialties.includes(m);
                const meta = METIER_META[m];
                return (
                  <button
                    key={m}
                    onClick={() => toggleSpecialty(m)}
                    className={`px-4 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                      isActive
                        ? "bg-white shadow border border-neutral-200"
                        : "text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100"
                    }`}
                    style={{ fontFamily: "Poppins", color: isActive ? meta.color : undefined }}
                  >
                    {meta.label}
                  </button>
                );
              })}
            </div>
            <div className="flex flex-row flex-wrap gap-1">
              {viewMode === "jour" ? (
                <>
                  <button
                    onClick={() => setSelectedWorkerIds(metierWorkers.map((w) => w.id))}
                    className={`px-4 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                      selectedWorkerIds.length === 0 || selectedWorkerIds.length === metierWorkers.length
                        ? "bg-white text-black shadow border border-neutral-200"
                        : "text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100"
                    }`}
                    style={{ fontFamily: "Poppins" }}
                  >
                    Tous
                  </button>
                  {metierWorkers.map((w) => {
                    const allSelected = selectedWorkerIds.length === 0 || selectedWorkerIds.length === metierWorkers.length;
                    const isActive = allSelected || selectedWorkerIds.includes(w.id);
                    return (
                      <button
                        key={w.id}
                        onClick={() => {
                          if (allSelected) {
                            // From "tous", clicking one worker selects only that one
                            setSelectedWorkerIds([w.id]);
                          } else {
                            toggleWorkerId(w.id);
                          }
                        }}
                        className={`px-4 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                          isActive
                            ? "bg-white text-black shadow border border-neutral-200"
                            : "text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100"
                        }`}
                        style={{ fontFamily: "Poppins" }}
                      >
                        {w.name}
                      </button>
                    );
                  })}
                </>
              ) : (
                <>
                  <button
                    onClick={() => setSelectedWeekWorkerId("tous")}
                    className={`px-4 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                      selectedWeekWorkerId === "tous"
                        ? "bg-white text-black shadow border border-neutral-200"
                        : "text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100"
                    }`}
                    style={{ fontFamily: "Poppins" }}
                  >
                    Tous
                  </button>
                  {metierWorkers.map((w) => {
                    const isActive = selectedWeekWorkerId === w.id;
                    return (
                      <button
                        key={w.id}
                        onClick={() => setSelectedWeekWorkerId(w.id)}
                        className={`px-4 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                          isActive
                            ? "bg-white text-black shadow border border-neutral-200"
                            : "text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100"
                        }`}
                        style={{ fontFamily: "Poppins" }}
                      >
                        {w.name}
                      </button>
                    );
                  })}
                </>
              )}
            </div>
            {viewMode === "jour" ? (
              <WorkerTimeGrid
                gridScrollRef={gridScrollRef}
                gridPreview={gridPreview}
                setGridPreview={setGridPreview}
                jobDragRef={jobDragRef}
                blocks={blocks}
                allMode={false}
                selectedWorkerIds={selectedWorkerIds.length > 0 && selectedWorkerIds.length < workers.length ? selectedWorkerIds : undefined}
              />
            ) : (
              <WeekTimeGridAtelier
                blocks={blocks}
                workerId={selectedWeekWorkerId || workers[0]?.id}
                gridScrollRef={gridScrollRef}
                gridPreview={gridPreview}
                setGridPreview={setGridPreview}
                jobDragRef={jobDragRef}
              />
            )}
          </div>
        </div>

        {floatingGhost && <FloatingGhostOverlay ghost={floatingGhost} />}
        <AtelierHints isDragging={!!(floatingGhost || gridPreview)} viewMode={viewMode} />
        {splitTarget && (
          <ChargeModal
            charge={splitTarget}
            mergeableCharges={charges.filter(
              (c) => c.ordre_id && c.ordre_id === splitTarget.ordre_id && c.id !== splitTarget.id,
            )}
            onClose={() => setSplitTarget(null)}
            onOpenOrdre={() => {
              if (splitTarget.ordre_id) {
                setSelectedOrdre(null);
                ordresApi.get(splitTarget.ordre_id).then(setSelectedOrdre).catch(() => {
                  showNotification({ type: "error", message: "Cet ordre n'existe plus" });
                });
              }
            }}
          />
        )}
        {selectedOrdre && (
          <OrdreModal
            ordre={selectedOrdre}
            onClose={() => setSelectedOrdre(null)}
            onSave={() => setSelectedOrdre(null)}
            onDelete={isOwnerOrAdmin(userData) ? () => setSelectedOrdre(null) : undefined}
          />
        )}
        {reportPausePending && (
          <ReportPauseModal
            targetStatus={reportPausePending.targetStatus}
            clientName={reportPausePending.clientName}
            onCancel={() => setReportPausePending(null)}
            onConfirm={(reason) => {
              moveChargeToStatus(reportPausePending.chargeId, reportPausePending.targetStatus);
              // Send WhatsApp notification via restitution change endpoint if charge is linked to an ordre
              const charge = charges.find((c) => c.id === reportPausePending.chargeId);
              if (charge?.ordre_id) {
                const statusLabel = reportPausePending.targetStatus === "reportee" ? "Reporté" : "En pause";
                ordresApi.notifyRestitutionChange(charge.ordre_id, {
                  new_date_seconds: Math.floor(Date.now() / 1000),
                  comment: `${statusLabel} : ${reason}`,
                  notify: true,
                }).catch(() => {});
              }
              setReportPausePending(null);
            }}
          />
        )}
        {lunchOverlapPending && (
          <LunchOverlapModal
            startHour={lunchOverlapPending.startHour}
            durationHours={lunchOverlapPending.durationHours}
            lunchStart={lunchOverlapPending.lunchStart}
            lunchEnd={lunchOverlapPending.lunchEnd}
            onCancel={() => setLunchOverlapPending(null)}
            onForce={() => {
              const p = lunchOverlapPending;
              if (p.type === "job" && p.jobId) {
                updateJob(p.jobId, { startHour: p.startHour, durationHours: p.durationHours, workerId: p.workerId, date: p.date });
              } else if (p.type === "charge" && p.chargeId) {
                placeChargeAsJob(p.chargeId, p.workerId, p.startHour, p.durationHours, p.date);
              }
              setLunchOverlapPending(null);
            }}
            onSplit={(before, after) => {
              const p = lunchOverlapPending;
              if (p.type === "job" && p.jobId) {
                // Update original job to the before-lunch portion
                updateJob(p.jobId, { startHour: before.startHour, durationHours: before.duration, workerId: p.workerId, date: p.date });
                // Create the after-lunch job optimistically, then persist via API
                const job = useAtelierStore.getState().jobs.find((j) => j.id === p.jobId);
                if (job) {
                  const orgId = job.org_id || "";
                  const carWindow = job.carWindow || (job.ordre_id ? useAtelierStore.getState().carWindows[job.ordre_id] : undefined);
                  const tempId = `split_${Date.now()}`;
                  const optimisticJob = {
                    id: tempId,
                    clientName: job.clientName,
                    plate: job.plate,
                    hours: after.duration,
                    workerId: p.workerId,
                    date: p.date,
                    startHour: after.startHour,
                    durationHours: after.duration,
                    status: job.status,
                    specialty: job.specialty,
                    ordre_id: job.ordre_id,
                    org_id: orgId,
                    carWindow,
                    vehicleName: job.vehicleName,
                    interventions: job.interventions,
                    hasCarIcon: job.hasCarIcon,
                  };
                  // Add to state immediately so it's visible
                  useAtelierStore.setState((s) => ({ jobs: [...s.jobs, optimisticJob as any] }));
                  // Persist via API, replace temp with real
                  import("@services/api/atelier").then(({ atelierApi }) => {
                    atelierApi.createJob({
                      clientName: job.clientName,
                      plate: job.plate,
                      hours: after.duration,
                      workerId: p.workerId,
                      date: p.date,
                      startHour: after.startHour,
                      durationHours: after.duration,
                      status: job.status,
                      specialty: job.specialty,
                      ordre_id: job.ordre_id,
                      org_id: orgId,
                      carWindow,
                    }).then((created) => {
                      useAtelierStore.setState((s) => ({
                        jobs: s.jobs.map((j) => j.id === tempId ? { ...created, carWindow: carWindow ?? created.carWindow } : j),
                      }));
                    });
                  });
                }
              } else if (p.type === "charge" && p.chargeId) {
                const charge = useAtelierStore.getState().charges.find((c) => c.id === p.chargeId);
                // Place the before-lunch portion (synchronous state update)
                placeChargeAsJob(p.chargeId, p.workerId, before.startHour, before.duration, p.date);
                // Place the after-lunch portion from the remaining charge immediately
                // After placeChargeAsJob, the original charge still exists with reduced hours (if total > before.duration)
                if (charge && charge.hours > before.duration) {
                  const remainingCharge = useAtelierStore.getState().charges.find((c) => c.id === p.chargeId);
                  if (remainingCharge) {
                    placeChargeAsJob(remainingCharge.id, p.workerId, after.startHour, after.duration, p.date);
                  }
                }
              }
              setLunchOverlapPending(null);
            }}
          />
        )}
        {jobMergePending && (
          <JobMergeModal
            draggedClientName={jobMergePending.draggedClientName}
            targetClientName={jobMergePending.targetClientName}
            mergedStartHour={jobMergePending.mergedStartHour}
            mergedDurationHours={jobMergePending.mergedDurationHours}
            onCancel={() => setJobMergePending(null)}
            onMerge={() => {
              const p = jobMergePending;
              mergeJobs(p.targetJobId, p.draggedJobId, p.mergedStartHour, p.mergedDurationHours);
              setJobMergePending(null);
            }}
          />
        )}
        {metierMismatchPending && (
          <MetierMismatchModal
            zoneMetier={metierMismatchPending.zoneMetier}
            magnetMetier={metierMismatchPending.magnetMetier}
            onCancel={() => setMetierMismatchPending(null)}
            onConfirm={confirmMetierMismatch}
          />
        )}
      </div>
    </div>
  );
}

// --- Onboarding hints ---

function HintBubble({ children, visible }: { children: React.ReactNode; visible: boolean }) {
  return (
    <div className={`fixed top-20 left-1/2 -translate-x-1/2 z-50 pointer-events-none transition-opacity duration-300 ${visible ? "opacity-100" : "opacity-0"}`}>
      <div className="bg-white text-neutral-800 rounded-2xl px-5 py-3 flex items-center gap-3 shadow-2xl border border-neutral-200">
        {children}
      </div>
    </div>
  );
}

function KeyIcon({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-neutral-100 border border-neutral-300 font-mono text-sm font-semibold">
      {children}
    </span>
  );
}

const HINT_SEQUENCE_IDLE = [
  { id: "arrows", ms: 3000, content: (
    <>
      <div className="flex items-center gap-1"><KeyIcon>&larr;</KeyIcon><KeyIcon>&rarr;</KeyIcon></div>
      <span className="text-h3 font-medium whitespace-nowrap">Utilisez les flèches pour naviguer</span>
    </>
  )},
  { id: "drag", ms: 3000, content: (
    <>
      <img src={hintDragIcon} alt="" className="w-6 h-6" />
      <span className="text-h3 font-medium whitespace-nowrap">Glissez-déposez les charges pour les planifier</span>
    </>
  )},
] as const;

const HINT_SEQUENCE_DRAGGING = [
  { id: "dragArrows", ms: 3000, content: (
    <>
      <div className="flex items-center gap-1"><KeyIcon>&larr;</KeyIcon><KeyIcon>&rarr;</KeyIcon></div>
      <span className="text-h3 font-medium whitespace-nowrap">Naviguez entre les semaines avec la charge</span>
    </>
  )},
  { id: "drop", ms: 3000, content: (
    <>
      <img src={hintDropGif} alt="" className="w-6 h-6" />
      <span className="text-h3 font-medium whitespace-nowrap">Relâchez pour placer la charge</span>
    </>
  )},
  { id: "returnCharge", ms: 3000, content: (
    <>
      <img src={hintLeftIcon} alt="" className="w-6 h-6" />
      <span className="text-h3 font-medium whitespace-nowrap">Glissez à gauche pour remettre dans les charges à répartir</span>
    </>
  )},
] as const;

function AtelierHints({ isDragging, viewMode }: { isDragging: boolean; viewMode: string }) {
  const [stepIndex, setStepIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const isDraggingRef = useRef(isDragging);
  const playedIdleRef = useRef(false);
  const playedDragRef = useRef(false);

  const sequence = isDragging ? HINT_SEQUENCE_DRAGGING : HINT_SEQUENCE_IDLE;

  // Reset sequence when dragging state changes, but only if not already played
  useEffect(() => {
    if (isDragging === isDraggingRef.current) return;
    isDraggingRef.current = isDragging;
    const alreadyPlayed = isDragging ? playedDragRef.current : playedIdleRef.current;
    if (alreadyPlayed) {
      setStepIndex(sequence.length); // skip all
      return;
    }
    setStepIndex(0);
    setVisible(true);
  }, [isDragging]);

  // Auto-advance through steps
  useEffect(() => {
    if (stepIndex >= sequence.length) {
      // Mark phase as played when we reach the end
      if (isDragging) playedDragRef.current = true;
      else playedIdleRef.current = true;
      return;
    }
    setVisible(true);
    const timer = setTimeout(() => {
      setVisible(false);
      setTimeout(() => setStepIndex((i) => i + 1), 300);
    }, sequence[stepIndex].ms);
    return () => clearTimeout(timer);
  }, [stepIndex, sequence]);

  if (stepIndex >= sequence.length) return null;

  return <HintBubble visible={visible}>{sequence[stepIndex].content}</HintBubble>;
}

// --- Compaction selector (row-height density), mirrors Planning Réception ---

const ATELIER_COMPACTION_OPTIONS: { value: CompactionMode; title: string; icon: React.ReactNode }[] = [
  {
    value: "compact",
    title: "Compact",
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <rect x="2" y="3" width="14" height="2" rx="1" fill="currentColor" />
        <rect x="2" y="8" width="14" height="2" rx="1" fill="currentColor" />
        <rect x="2" y="13" width="14" height="2" rx="1" fill="currentColor" />
      </svg>
    ),
  },
  {
    value: "normal",
    title: "Normal",
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <rect x="2" y="2" width="14" height="3" rx="1" fill="currentColor" />
        <rect x="2" y="7.5" width="14" height="3" rx="1" fill="currentColor" />
        <rect x="2" y="13" width="14" height="3" rx="1" fill="currentColor" />
      </svg>
    ),
  },
  {
    value: "spread",
    title: "Large",
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <rect x="2" y="1" width="14" height="4" rx="1" fill="currentColor" />
        <rect x="2" y="7" width="14" height="4" rx="1" fill="currentColor" />
        <rect x="2" y="13" width="14" height="4" rx="1" fill="currentColor" />
      </svg>
    ),
  },
];

function AtelierCompactionSelector({
  compaction,
  setCompaction,
}: {
  compaction: CompactionMode;
  setCompaction: (m: CompactionMode) => void;
}) {
  return (
    <div className="flex bg-gray-100 rounded-lg p-0.5 flex-shrink-0" title="Hauteur des lignes">
      {ATELIER_COMPACTION_OPTIONS.map((opt) => (
        <button
          key={opt.value}
          onClick={() => setCompaction(opt.value)}
          title={opt.title}
          className={`flex items-center justify-center w-8 h-8 rounded-md transition-colors ${
            compaction === opt.value
              ? "bg-white shadow text-neutral-900"
              : "text-neutral-400 hover:text-neutral-600"
          }`}
        >
          {opt.icon}
        </button>
      ))}
    </div>
  );
}
